import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  TrendingUp, RefreshCw, Loader, Save, Download, AlertTriangle, CheckCircle2,
  CalendarDays, Users, Package, Target, ClipboardList, RotateCcw, Plus, Trash2,
} from 'lucide-react';
import { useApp } from '../context/AppContext';
import {
  buildForecast, reviewSeason, saturdays, sundays,
} from '../utils/peakPlan';
import { exportAgreement } from '../utils/peakExport';

// Peak Planning — the Australia Post "Peak Period Forecasting Agreement" for the
// season, built off what the depot actually delivered rather than off last year's
// spreadsheet.
//
// Three tabs, in the order the conversation happens:
//   Plan          — the AP form itself: base data, uplift, drivers, weekend days,
//                   subsidies, comments. Exports as the .xlsx that goes to the BP.
//   Peak Review   — last season's plan measured against the actuals, so the meeting
//                   starts from how good the last forecast was.
//   Scope of Works — everything peak needs besides a volume number: drivers, vans,
//                   sorters, express post, compliance, billing — owner, due, status.
//
// Actuals come from wahr_history through peak-planning-sb. That is the same measure
// AP's own base data uses (their June 2025 base of 7,512.65 vs our 7,514.6 actual),
// which is why a variance on this page is a real variance.

const API = '/.netlify/functions/peak-planning-sb';
const api = (action, payload = {}) =>
  fetch(API, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action, ...payload }) })
    .then((r) => r.json());

const n0 = (v) => (Number.isFinite(Number(v)) ? Math.round(Number(v)).toLocaleString() : '—');
const n1 = (v) => (Number.isFinite(Number(v)) ? Number(v).toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 }) : '—');
const pct = (v, dp = 1) => (Number.isFinite(Number(v)) ? `${Number(v) >= 0 ? '+' : ''}${Number(v).toFixed(dp)}%` : '—');
const fmtDay = (iso) => (iso ? new Date(`${iso}T00:00:00`).toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' }) : '—');
const fmtDate = (iso) => (iso ? new Date(`${iso}T00:00:00`).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : '—');

const card = { background: 'var(--hp-card)', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', padding: 16 };
const label = { display: 'block', fontSize: '.68rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--hp-dim)', marginBottom: 4 };
const th = { textAlign: 'left', padding: '8px 10px', fontSize: '.66rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--hp-dim)', borderBottom: '1px solid var(--hp-border)', whiteSpace: 'nowrap' };
const td = { padding: '8px 10px', fontSize: '.85rem', borderBottom: '1px solid var(--hp-border)' };
const num = { ...td, textAlign: 'right', fontFamily: 'var(--font-mono)' };
const sectionTitle = { fontSize: '.8rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--hp-gold)', margin: '0 0 12px' };
// Wide tables scroll inside their own box — the page body never scrolls sideways
// on a phone (see the ≤768px rules in global.css).
const scrollBox = { overflowX: 'auto', WebkitOverflowScrolling: 'touch' };
// .input sizes to its content by default — force full width inside a grid cell so a
// long workstream title is not clipped to twenty characters.
const wide = { width: '100%', boxSizing: 'border-box' };

const Stat = ({ icon: Icon, title, value, sub, tone }) => (
  <div style={{ ...card, flex: '1 1 180px', minWidth: 160 }}>
    <div style={{ ...label, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
      {Icon && <Icon size={12} />}{title}
    </div>
    <div style={{ fontSize: '1.5rem', fontWeight: 800, fontFamily: 'var(--font-mono)', color: tone || 'var(--hp-text)', lineHeight: 1.1 }}>{value}</div>
    {sub && <div style={{ fontSize: '.72rem', color: 'var(--hp-dim)', marginTop: 4 }}>{sub}</div>}
  </div>
);

const Chip = ({ v, dp = 1, invert }) => {
  if (!Number.isFinite(Number(v))) return <span style={{ color: 'var(--hp-dim)' }}>—</span>;
  const good = invert ? Number(v) <= 0 : Math.abs(Number(v)) <= 3;
  const colour = good ? 'var(--hp-green)' : Math.abs(Number(v)) <= 8 ? 'var(--hp-amber)' : 'var(--hp-red)';
  return (
    <span style={{ fontFamily: 'var(--font-mono)', fontSize: '.75rem', color: colour, border: `1px solid ${colour}`, borderRadius: 5, padding: '1px 6px', whiteSpace: 'nowrap' }}>
      {pct(v, dp)}
    </span>
  );
};

export default function PeakPlanningPage() {
  const { addNotification } = useApp();
  const notify = useCallback((msg, type = 'success') => {
    if (addNotification) addNotification(msg, type); else if (type === 'error') console.error(msg);
  }, [addNotification]);

  const thisYear = new Date().getFullYear();
  // Before November the season being planned is this year's; from January the
  // interesting season is the one just finished.
  const [year, setYear] = useState(thisYear);
  const [tab, setTab] = useState('plan');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const [plan, setPlan] = useState(null);
  const [scope, setScope] = useState([]);
  const [daily, setDaily] = useState({});
  const [apUplift, setApUplift] = useState(null);
  const [seasons, setSeasons] = useState([]);
  const [updated, setUpdated] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [scopeDirty, setScopeDirty] = useState(false);

  const load = useCallback(async (y) => {
    setLoading(true); setErr('');
    try {
      const r = await api('load', { year: y });
      if (!r.success) throw new Error(r.error || 'Load failed');
      setPlan(r.plan); setScope(r.scope || []); setDaily(r.daily || {});
      setApUplift(r.apUplift); setSeasons(r.seasons || []); setUpdated(r.updated || null);
      setDirty(false); setScopeDirty(false);
    } catch (e) { setErr(e.message); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(year); }, [load, year]);

  const setField = (patch) => { setPlan((p) => ({ ...p, ...patch })); setDirty(true); };

  const savePlan = async () => {
    setSaving(true);
    try {
      const r = await api('savePlan', { year, plan });
      if (!r.success) throw new Error(r.error || 'Save failed');
      setUpdated(r.updated); setDirty(false); notify('Peak plan saved');
    } catch (e) { notify(e.message, 'error'); } finally { setSaving(false); }
  };
  const saveScope = async (next) => {
    setSaving(true);
    try {
      const r = await api('saveScope', { year, scope: next || scope });
      if (!r.success) throw new Error(r.error || 'Save failed');
      setUpdated(r.updated); setScopeDirty(false); notify('Scope of works saved');
    } catch (e) { notify(e.message, 'error'); } finally { setSaving(false); }
  };

  const forecast = useMemo(() => (plan ? buildForecast(plan, daily, apUplift) : null), [plan, daily, apUplift]);
  // The review always looks at the season BEFORE the one being planned — that is the
  // reflection the meeting opens with.
  const [reviewYear, setReviewYear] = useState(thisYear - 1);
  useEffect(() => { setReviewYear(year - 1); }, [year]);
  const [reviewPlan, setReviewPlan] = useState(null);
  useEffect(() => {
    let live = true;
    api('load', { year: reviewYear }).then((r) => { if (live && r.success) setReviewPlan(r.plan); });
    return () => { live = false; };
  }, [reviewYear]);
  const review = useMemo(
    () => (reviewPlan && Object.keys(daily).length ? reviewSeason(reviewPlan, daily) : null),
    [reviewPlan, daily],
  );

  const yearOptions = useMemo(() => {
    const set = new Set([thisYear, thisYear + 1, ...seasons]);
    return [...set].sort((a, b) => b - a);
  }, [seasons, thisYear]);

  return (
    <div>
      <div className="page-header">
        <div className="page-header-left">
          <h2 style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <TrendingUp size={22} style={{ color: 'var(--hp-gold)' }} /> Peak Planning
          </h2>
          <p>
            The AP peak forecasting agreement, built off what we actually delivered
            {updated && updated.at ? ` · saved ${fmtDate(updated.at.slice(0, 10))}${updated.by ? ` by ${updated.by}` : ''}` : ''}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <select className="input" style={{ width: 'auto' }} value={year} onChange={(e) => setYear(Number(e.target.value))}>
            {yearOptions.map((y) => <option key={y} value={y}>Peak {y}</option>)}
          </select>
          <button className="btn btn-secondary btn-sm" onClick={() => load(year)} title="Refresh"><RefreshCw size={14} /></button>
        </div>
      </div>

      <div className="page-body">
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 16 }}>
          {[['plan', `Plan ${year}`], ['review', `Peak ${reviewYear} Review`], ['scope', 'Scope of Works']].map(([k, l]) => (
            <button key={k} className={`btn btn-sm ${tab === k ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setTab(k)}>{l}</button>
          ))}
          <div style={{ flex: 1 }} />
          {tab === 'plan' && dirty && (
            <button className="btn btn-primary btn-sm" onClick={savePlan} disabled={saving}>
              <Save size={14} /> {saving ? 'Saving…' : 'Save plan'}
            </button>
          )}
          {tab === 'scope' && scopeDirty && (
            <button className="btn btn-primary btn-sm" onClick={() => saveScope()} disabled={saving}>
              <Save size={14} /> {saving ? 'Saving…' : 'Save scope'}
            </button>
          )}
        </div>

        {err && (
          <div style={{ ...card, borderColor: 'var(--hp-red)', color: 'var(--hp-red)', marginBottom: 16, display: 'flex', gap: 8, alignItems: 'center' }}>
            <AlertTriangle size={16} /> {err}
          </div>
        )}
        {loading && <div style={{ padding: 40, textAlign: 'center', color: 'var(--hp-dim)' }}><Loader size={18} /> Loading actuals…</div>}

        {!loading && plan && tab === 'plan' && (
          <PlanTab plan={plan} setField={setField} forecast={forecast} notify={notify} />
        )}
        {!loading && tab === 'review' && (
          <ReviewTab review={review} reviewYear={reviewYear} setReviewYear={setReviewYear} seasons={seasons} plan={plan} forecast={forecast} />
        )}
        {!loading && tab === 'scope' && (
          <ScopeTab
            scope={scope} year={year}
            onChange={(next) => { setScope(next); setScopeDirty(true); }}
            onReset={async () => {
              const r = await api('resetScope', { year });
              if (r.success) { setScope(r.scope); setScopeDirty(false); notify('Scope reset to the standard peak workstreams'); }
              else notify(r.error || 'Reset failed', 'error');
            }}
          />
        )}
      </div>
    </div>
  );
}

// ── Plan tab ─────────────────────────────────────────────────────────────────
function PlanTab({ plan, setField, forecast, notify }) {
  const f = forecast;
  const year = Number(plan.year);
  const app = Number(plan.articles_per_driver) || 1;

  const weekendDays = useMemo(() => ([
    ...saturdays(year, 10).map((iso) => ({ iso, kind: 'Saturday', month: 'November' })),
    ...sundays(year, 10).map((iso) => ({ iso, kind: 'Sunday', month: 'November' })),
    ...saturdays(year, 11).map((iso) => ({ iso, kind: 'Saturday', month: 'December' })),
    ...sundays(year, 11).map((iso) => ({ iso, kind: 'Sunday', month: 'December' })),
  ].sort((a, b) => (a.iso < b.iso ? -1 : 1))), [year]);

  const setWeekend = (iso, patch) => {
    const wk = { ...(plan.weekend || {}) };
    wk[iso] = { operating: false, drivers: 0, ...(wk[iso] || {}), ...patch };
    setField({ weekend: wk });
  };

  const exportForm = () => {
    try { exportAgreement(plan, f); notify('AP agreement exported'); }
    catch (e) { notify(e.message || 'Export failed', 'error'); }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* headline */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <Stat icon={Package} title={`Peak ${year} forecast`} value={n0(f && f.total)} sub={`Nov ${n0(f && f.nov.monthly)} · Dec ${n0(f && f.dec.monthly)}`} tone="var(--hp-gold)" />
        <Stat icon={TrendingUp} title="Vs last peak actual" value={f && f.last.has ? pct(f.growthVsLast) : '—'} sub={f && f.last.has ? `${n0(f.last.total)} delivered in peak ${year - 1}` : 'no actuals yet'} />
        <Stat icon={Users} title="Drivers needed · Dec" value={f ? n1(f.dec.driversNeeded) : '—'} sub={`at ${n0(app)} articles/driver · plan has ${f ? f.drivers.total : 0}`} tone={f && f.drivers.shortfallDec > 0 ? 'var(--hp-red)' : 'var(--hp-green)'} />
        <Stat icon={Target} title="Busiest day" value={f && f.peakDay ? n0(f.peakDay.parcels) : '—'} sub={f && f.peakDay ? `${fmtDay(f.peakDay.was.iso)} carried ${n0(f.peakDay.was.parcels)} on ${f.peakDay.was.drivers} drivers` : '—'} />
      </div>

      {/* target vs built */}
      {f && f.target !== null && (
        <div style={{ ...card, borderColor: Math.abs(f.targetGap) / (f.target || 1) > 0.02 ? 'var(--hp-amber)' : 'var(--hp-green)' }}>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'baseline' }}>
            <div>
              <div style={label}>Target — last peak +{n1(plan.growth_pct)}%</div>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: '1.1rem', fontWeight: 700 }}>{n0(f.target)}</div>
            </div>
            <div>
              <div style={label}>This plan carries</div>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: '1.1rem', fontWeight: 700 }}>{n0(f.total)}</div>
            </div>
            <div>
              <div style={label}>Difference</div>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: '1.1rem', fontWeight: 700, color: f.targetGap >= 0 ? 'var(--hp-green)' : 'var(--hp-red)' }}>
                {f.targetGap >= 0 ? '+' : ''}{n0(f.targetGap)}
              </div>
            </div>
            <div style={{ flex: '1 1 260px', fontSize: '.78rem', color: 'var(--hp-dim)' }}>
              The plan reaches the target through the calendar: {f.nov.weekdayDays + f.dec.weekdayDays} weekdays
              (public holidays already removed) plus the {f.nov.weekendDays + f.dec.weekendDays} weekend day(s) ticked below.
              Tick or untick a Saturday and this moves.
            </div>
          </div>
        </div>
      )}

      {/* agreement header */}
      <div style={card}>
        <h3 style={sectionTitle}>Agreement details</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
          <div><span style={label}>Mail service number</span><input className="input" style={wide} value={plan.mail_service_number || ''} onChange={(e) => setField({ mail_service_number: e.target.value })} /></div>
          <div><span style={label}>Mail service name</span><input className="input" style={wide} value={plan.mail_service_name || ''} onChange={(e) => setField({ mail_service_name: e.target.value })} /></div>
          <div><span style={label}>PDC / DC facility</span><input className="input" style={wide} value={plan.facility || ''} onChange={(e) => setField({ facility: e.target.value })} /></div>
          <div><span style={label}>Base month</span><input className="input" style={wide} type="month" value={plan.base_month || ''} onChange={(e) => setField({ base_month: e.target.value })} /></div>
        </div>
      </div>

      {/* basis + base data */}
      <div style={card}>
        <h3 style={sectionTitle}>Base data &amp; forecast basis</h3>
        {f && f.base ? (
          <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', marginBottom: 14, fontSize: '.85rem' }}>
            <div><span style={label}>{f.base.label} actual (weekday)</span><b style={{ fontFamily: 'var(--font-mono)' }}>{n0(f.base.daily)}</b> /day</div>
            <div><span style={label}>Logins on the road</span><b style={{ fontFamily: 'var(--font-mono)' }}>{n1(f.base.drivers)}</b></div>
            <div><span style={label}>Articles per driver</span><b style={{ fontFamily: 'var(--font-mono)' }}>{n1(f.base.articlesPerDriver)}</b></div>
            <div><span style={label}>Month total</span><b style={{ fontFamily: 'var(--font-mono)' }}>{n0(f.base.total)}</b> over {f.base.days} days</div>
          </div>
        ) : (
          <div style={{ color: 'var(--hp-amber)', fontSize: '.85rem', marginBottom: 12 }}>
            No actuals for {plan.base_month} — import the WAHR month or pick another base.
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
          <div>
            <span style={label}>Forecast basis</span>
            <select className="input" style={wide} value={plan.basis || 'actual'} onChange={(e) => setField({ basis: e.target.value })}>
              <option value="actual">Last peak actual + growth %</option>
              <option value="ap">June base × AP uplift factors</option>
            </select>
          </div>
          <div>
            <span style={label}>Growth on last peak (%)</span>
            <input className="input" style={wide} type="number" step="0.1" value={plan.growth_pct ?? ''} onChange={(e) => setField({ growth_pct: e.target.value === '' ? '' : Number(e.target.value) })} />
          </div>
          <div>
            <span style={label}>Articles per driver (planning rate)</span>
            <input className="input" style={wide} type="number" step="1" value={plan.articles_per_driver ?? ''} onChange={(e) => setField({ articles_per_driver: e.target.value === '' ? '' : Number(e.target.value) })} />
          </div>
        </div>

        {f && (
          <div style={{ marginTop: 12, fontSize: '.78rem', color: 'var(--hp-dim)', lineHeight: 1.5 }}>
            The other basis ({f.alternate.basis === 'ap' ? `June base × AP's own uplift (Nov ×${f.uplift.nov.toFixed(3)}, Dec ×${f.uplift.dec.toFixed(3)})` : `last peak actual +${n1(plan.growth_pct)}%`})
            gives <b style={{ color: 'var(--hp-text)', fontFamily: 'var(--font-mono)' }}>{n0(f.alternate.total)}</b> for the season
            — {pct(((f.alternate.total / (f.total || 1)) - 1) * 100)} against the basis in use. Both are on the page on purpose:
            AP's uplift model only grows if the BAU base grows.
          </div>
        )}
      </div>

      {/* forecast table */}
      <div style={card}>
        <h3 style={sectionTitle}>Forecast</h3>
        <div style={scrollBox}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 540 }}>
            <thead><tr><th style={th}> </th><th style={{ ...th, textAlign: 'right' }}>November {year}</th><th style={{ ...th, textAlign: 'right' }}>December {year}</th><th style={{ ...th, textAlign: 'right' }}>Season</th></tr></thead>
            <tbody>
              {f && [
                ['Daily volume (weekday)', n0(f.nov.daily), n0(f.dec.daily), ''],
                ['Saturday volume', n0(f.nov.saturdayDaily), n0(f.dec.saturdayDaily), ''],
                ['Operating weekdays', f.nov.weekdayDays, f.dec.weekdayDays, f.nov.weekdayDays + f.dec.weekdayDays],
                ['Weekend days planned', f.nov.weekendDays, f.dec.weekendDays, f.nov.weekendDays + f.dec.weekendDays],
                ['Total monthly volume', n0(f.nov.monthly), n0(f.dec.monthly), n0(f.total)],
                ['Daily drivers (logins) required', n1(f.nov.driversNeeded), n1(f.dec.driversNeeded), ''],
                ['Variance to plan head count', n1(f.drivers.shortfallNov), n1(f.drivers.shortfallDec), ''],
              ].map((r) => (
                <tr key={r[0]}>
                  <td style={td}>{r[0]}</td>
                  <td style={num}>{r[1]}</td><td style={num}>{r[2]}</td><td style={{ ...num, color: 'var(--hp-dim)' }}>{r[3]}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {f && f.peakDay && (
          <div style={{ marginTop: 12, padding: 12, borderRadius: 'var(--radius)', background: 'var(--hp-surface)', fontSize: '.8rem', lineHeight: 1.6 }}>
            <b>The day that decides the plan.</b> Last season's busiest day was {fmtDay(f.peakDay.was.iso)} —
            {' '}{n0(f.peakDay.was.parcels)} parcels on {f.peakDay.was.drivers} logins, {n0(f.peakDay.was.perDriver)} articles each.
            Grown the same way as this forecast that day becomes <b style={{ fontFamily: 'var(--font-mono)' }}>{n0(f.peakDay.parcels)}</b>:
            {' '}<b>{n1(f.peakDay.driversNeeded)}</b> drivers at the {n0(app)} planning rate, or <b>{n1(f.peakDay.driversAtLastRate)}</b> if
            the drivers carry what they carried last December. The plan has {f.drivers.total}.
          </div>
        )}
      </div>

      {/* drivers */}
      <div style={card}>
        <h3 style={sectionTitle}>Driver plan</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12 }}>
          <div><span style={label}>Existing BAU standard</span><input className="input" style={wide} type="number" value={plan.bau_drivers ?? ''} onChange={(e) => setField({ bau_drivers: Number(e.target.value) })} /></div>
          <div><span style={label}>Existing BAU XL</span><input className="input" style={wide} type="number" value={plan.bau_xl ?? ''} onChange={(e) => setField({ bau_xl: Number(e.target.value) })} /></div>
          <div><span style={label}>Additional peak standard</span><input className="input" style={wide} type="number" value={plan.extra_drivers ?? ''} onChange={(e) => setField({ extra_drivers: Number(e.target.value) })} /></div>
          <div><span style={label}>Additional peak XL</span><input className="input" style={wide} type="number" value={plan.extra_xl ?? ''} onChange={(e) => setField({ extra_xl: Number(e.target.value) })} /></div>
        </div>
        {f && (
          <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', marginTop: 14, fontSize: '.85rem' }}>
            <div><span style={label}>Total drivers for peak</span><b style={{ fontFamily: 'var(--font-mono)', fontSize: '1.1rem' }}>{f.drivers.total}</b></div>
            <div><span style={label}>Capacity · November</span><b style={{ fontFamily: 'var(--font-mono)' }}>{n0(f.capacity.nov)}</b> <span style={{ color: f.capacity.novVariance >= 0 ? 'var(--hp-green)' : 'var(--hp-red)' }}>({f.capacity.novVariance >= 0 ? '+' : ''}{n0(f.capacity.novVariance)})</span></div>
            <div><span style={label}>Capacity · December</span><b style={{ fontFamily: 'var(--font-mono)' }}>{n0(f.capacity.dec)}</b> <span style={{ color: f.capacity.decVariance >= 0 ? 'var(--hp-green)' : 'var(--hp-red)' }}>({f.capacity.decVariance >= 0 ? '+' : ''}{n0(f.capacity.decVariance)})</span></div>
          </div>
        )}
        <div style={{ marginTop: 10, fontSize: '.75rem', color: 'var(--hp-dim)' }}>
          Capacity = total drivers × articles per driver × operating days. The bracket is headroom against the forecast for that month.
        </div>
      </div>

      {/* weekend ops */}
      <div style={card}>
        <h3 style={sectionTitle}><CalendarDays size={13} style={{ verticalAlign: -2 }} /> Weekend PDC deliveries planned</h3>
        <div style={scrollBox}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 460 }}>
            <thead><tr><th style={th}>Date</th><th style={th}>Day</th><th style={th}>Operating</th><th style={{ ...th, textAlign: 'right' }}>No of drivers</th><th style={{ ...th, textAlign: 'right' }}>Same weekend last year</th></tr></thead>
            <tbody>
              {weekendDays.map((d) => {
                const w = (plan.weekend || {})[d.iso] || {};
                return (
                  <tr key={d.iso}>
                    <td style={td}>{fmtDay(d.iso)}</td>
                    <td style={{ ...td, color: 'var(--hp-dim)' }}>{d.kind}</td>
                    <td style={td}>
                      <input type="checkbox" checked={!!w.operating} onChange={(e) => setWeekend(d.iso, { operating: e.target.checked })} />
                    </td>
                    <td style={num}>
                      <input className="input" type="number" style={{ width: 90, textAlign: 'right' }} value={w.drivers ?? 0}
                        onChange={(e) => setWeekend(d.iso, { drivers: Number(e.target.value) })} disabled={!w.operating} />
                    </td>
                    <td style={{ ...num, color: 'var(--hp-dim)' }}>{lastYearWeekend(d.iso, f)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* subsidies */}
      <div style={card}>
        <h3 style={sectionTitle}>Subsidy summary</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12 }}>
          <div><span style={label}>PVA supported peak vans</span><input className="input" style={wide} type="number" value={plan.subsidy?.vans ?? 0} onChange={(e) => setField({ subsidy: { ...plan.subsidy, vans: Number(e.target.value) } })} /></div>
          <div><span style={label}>Proposed rate</span><input className="input" style={wide} type="number" step="0.01" value={plan.subsidy?.rate ?? 0} onChange={(e) => setField({ subsidy: { ...plan.subsidy, rate: Number(e.target.value) } })} /></div>
          <div><span style={label}>XL supported vans</span><input className="input" style={wide} type="number" value={plan.subsidy?.xl_vans ?? 0} onChange={(e) => setField({ subsidy: { ...plan.subsidy, xl_vans: Number(e.target.value) } })} /></div>
          <div><span style={label}>XL proposed rate</span><input className="input" style={wide} type="number" step="0.01" value={plan.subsidy?.xl_rate ?? 0} onChange={(e) => setField({ subsidy: { ...plan.subsidy, xl_rate: Number(e.target.value) } })} /></div>
        </div>
        <div style={{ marginTop: 12 }}>
          <span style={label}>Additional driver comments</span>
          <textarea className="input" style={wide} rows={2} value={plan.subsidy?.comments || ''} onChange={(e) => setField({ subsidy: { ...plan.subsidy, comments: e.target.value } })} />
        </div>
        <div style={{ marginTop: 8, fontSize: '.75rem', color: 'var(--hp-dim)' }}>
          Check the supported subsidy does not exceed the suggested total for the contract, and state the subsidy period. Any split of labour vs vehicle hire on an XL goes in the comment.
        </div>
      </div>

      {/* comments */}
      <div style={card}>
        <h3 style={sectionTitle}>Comments</h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {[
            ['staging', 'Staging / reload options · XL and van subsidy requirements'],
            ['sorting', 'Indoor sorting — head count, shift times, start and finish dates'],
            ['express', 'Express Post arrangements'],
            ['capacity', 'Opportunities to create capacity'],
          ].map(([k, l]) => (
            <div key={k}>
              <span style={label}>{l}</span>
              <textarea className="input" style={wide} rows={2} value={plan.comments?.[k] || ''} onChange={(e) => setField({ comments: { ...plan.comments, [k]: e.target.value } })} />
            </div>
          ))}
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button className="btn btn-secondary" onClick={exportForm}><Download size={15} /> Export AP agreement (.xlsx)</button>
      </div>
    </div>
  );
}

// What the equivalent weekend day carried last season — the nearest same-weekday
// date a year back, so a ticked Saturday can be sanity-checked against a real one.
function lastYearWeekend(iso, f) {
  if (!f || !f.last || !f.last.days.length) return '—';
  const target = new Date(`${iso}T00:00:00Z`).getTime() - 364 * 86400000; // 52 weeks keeps the weekday
  const want = new Date(target).toISOString().slice(0, 10);
  const hit = f.last.days.find((d) => d.iso === want);
  return hit ? `${n0(hit.parcels)} · ${hit.drivers} drv` : 'did not run';
}

// ── Review tab ───────────────────────────────────────────────────────────────
function ReviewTab({ review, reviewYear, setReviewYear, seasons, plan, forecast }) {
  if (!review || !review.actual.has) {
    return (
      <div style={{ ...card, color: 'var(--hp-dim)' }}>
        No actuals for peak {reviewYear}. Import the November and December WAHR months on the Import WAHR page and this fills in.
      </div>
    );
  }
  const a = review.actual;
  const maxWeek = Math.max(...a.weeks.map((w) => w.total), 1);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <span style={{ ...label, marginBottom: 0 }}>Season</span>
        <select className="input" style={{ width: 'auto' }} value={reviewYear} onChange={(e) => setReviewYear(Number(e.target.value))}>
          {(seasons.length ? seasons : [reviewYear]).map((y) => <option key={y} value={y}>Peak {y}</option>)}
        </select>
      </div>

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <Stat icon={Package} title={`Delivered · peak ${reviewYear}`} value={n0(a.total)} sub={`${a.operatingDays} operating days · Nov ${n0(a.nov.total)} · Dec ${n0(a.dec.total)}`} tone="var(--hp-gold)" />
        <Stat icon={Target} title="Against the agreed plan" value={pct(review.seasonVariance)} sub={review.seasonVariance === null ? '—' : `${n0(a.total)} delivered vs ${n0(a.total / (1 + review.seasonVariance / 100))} agreed`} tone={Math.abs(review.seasonVariance || 0) <= 3 ? 'var(--hp-green)' : 'var(--hp-amber)'} />
        <Stat icon={TrendingUp} title="Growth on the season before" value={pct(review.realisedGrowth)} sub={review.plannedGrowth ? `AP planned ${pct(review.plannedGrowth)}` : '—'} />
        <Stat icon={Users} title="Busiest day" value={a.busiest[0] ? n0(a.busiest[0].parcels) : '—'} sub={a.busiest[0] ? `${fmtDay(a.busiest[0].iso)} · ${a.busiest[0].drivers} logins · ${n0(a.busiest[0].perDriver)} each` : '—'} />
      </div>

      {review.baseCheck && (
        <div style={{ ...card, display: 'flex', gap: 10, alignItems: 'flex-start', borderColor: Math.abs(review.baseCheck.variance) < 1 ? 'var(--hp-green)' : 'var(--hp-amber)' }}>
          <CheckCircle2 size={16} style={{ color: Math.abs(review.baseCheck.variance) < 1 ? 'var(--hp-green)' : 'var(--hp-amber)', flexShrink: 0, marginTop: 2 }} />
          <div style={{ fontSize: '.82rem', lineHeight: 1.6 }}>
            <b>Base-data check.</b> AP built the {reviewYear} form on {n0(review.baseCheck.plan)} parcels a day.
            Our own WAHR actual for that month is {n0(review.baseCheck.actual)} — {pct(review.baseCheck.variance, 2)}.
            {Math.abs(review.baseCheck.variance) < 1
              ? ' We are counting the same parcels, so every variance below is a real variance and not a definition problem.'
              : ' That gap is worth raising before any other number on this page is argued about.'}
          </div>
        </div>
      )}

      <div style={card}>
        <h3 style={sectionTitle}>Agreed plan vs what we delivered</h3>
        <div style={scrollBox}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 560 }}>
            <thead><tr><th style={th}>Measure</th><th style={{ ...th, textAlign: 'right' }}>Agreed</th><th style={{ ...th, textAlign: 'right' }}>Actual</th><th style={{ ...th, textAlign: 'right' }}>Variance</th></tr></thead>
            <tbody>
              {review.rows.map((r) => (
                <tr key={r.key}>
                  <td style={td}>{r.label}<div style={{ fontSize: '.7rem', color: 'var(--hp-dim)' }}>{r.unit}</div></td>
                  <td style={num}>{r.unit === 'logins' || r.unit === 'articles' ? n1(r.plan) : n0(r.plan)}</td>
                  <td style={num}>{r.unit === 'logins' || r.unit === 'articles' ? n1(r.actual) : n0(r.actual)}</td>
                  <td style={{ ...num }}>{r.informational ? <span style={{ color: 'var(--hp-dim)', fontSize: '.75rem' }}>{pct(r.variance)}</span> : <Chip v={r.variance} />}</td>
                </tr>
              ))}
              {!review.rows.length && <tr><td style={td} colSpan={4}>No agreed figures stored for peak {reviewYear}.</td></tr>}
            </tbody>
          </table>
        </div>
        <div style={{ marginTop: 10, fontSize: '.75rem', color: 'var(--hp-dim)', lineHeight: 1.6 }}>
          Daily rates are compared on WEEKDAYS only — AP's "daily volume" is a weekday rate, and Saturdays ran at
          roughly {a.nov.weekday.avg ? Math.round((a.dec.saturday.avg || a.nov.saturday.avg) / (a.dec.weekday.avg || a.nov.weekday.avg) * 100) : 0}% of a weekday.
          The monthly totals include every day we ran.
        </div>
      </div>

      <div style={card}>
        <h3 style={sectionTitle}>The shape of the season</h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {a.weeks.map((w) => (
            <div key={w.wc} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 92, flexShrink: 0, fontSize: '.72rem', color: 'var(--hp-dim)', fontFamily: 'var(--font-mono)' }}>w/c {fmtDay(w.wc).replace(/^\w+ /, '')}</div>
              <div style={{ flex: 1, background: 'var(--hp-surface)', borderRadius: 4, overflow: 'hidden', height: 18 }}>
                <div style={{ width: `${(w.total / maxWeek) * 100}%`, height: '100%', background: w.total === maxWeek ? 'var(--hp-gold)' : 'var(--hp-blue)' }} />
              </div>
              <div style={{ width: 96, textAlign: 'right', fontFamily: 'var(--font-mono)', fontSize: '.78rem' }}>{n0(w.total)}</div>
              <div style={{ width: 62, textAlign: 'right', fontFamily: 'var(--font-mono)', fontSize: '.72rem', color: 'var(--hp-dim)' }}>{w.days}d</div>
            </div>
          ))}
        </div>
      </div>

      <div style={card}>
        <h3 style={sectionTitle}>The ten days that needed the drivers</h3>
        <div style={scrollBox}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 460 }}>
            <thead><tr><th style={th}>Day</th><th style={{ ...th, textAlign: 'right' }}>Parcels</th><th style={{ ...th, textAlign: 'right' }}>Logins</th><th style={{ ...th, textAlign: 'right' }}>Articles each</th></tr></thead>
            <tbody>
              {a.busiest.map((d) => (
                <tr key={d.iso}>
                  <td style={td}>{fmtDay(d.iso)}</td>
                  <td style={num}>{n0(d.parcels)}</td>
                  <td style={num}>{d.drivers}</td>
                  <td style={num}>{n0(d.perDriver)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <TalkingPoints review={review} plan={plan} forecast={forecast} />
    </div>
  );
}

// The meeting opener: the same numbers, said in sentences, so nobody has to read a
// table out loud. Everything here is derived — nothing is typed in.
function TalkingPoints({ review, plan, forecast }) {
  const a = review.actual;
  const pts = [];
  if (review.seasonVariance !== null) {
    pts.push(`Peak ${review.year} delivered ${n0(a.total)} parcels across ${a.operatingDays} operating days — ${pct(review.seasonVariance)} against the ${n0(a.total / (1 + review.seasonVariance / 100))} we agreed with AP. The forecast was good.`);
  }
  const novRow = review.rows.find((r) => r.key === 'nov_daily');
  const decRow = review.rows.find((r) => r.key === 'dec_daily');
  if (novRow && decRow) {
    pts.push(`On the weekday rate AP ran slightly hot both months — November ${pct(novRow.variance)}, December ${pct(decRow.variance)} — but the extra Saturdays put the monthly totals back above plan. Volume landed where they said; it just arrived on more days.`);
  }
  if (review.realisedGrowth !== null && review.plannedGrowth) {
    pts.push(`AP asked for ${pct(review.plannedGrowth)} growth on the season before and the network delivered ${pct(review.realisedGrowth)}. Worth holding that up against this year's ask.`);
  }
  if (forecast && forecast.base && forecast.last.has) {
    const bau = forecast.base.daily;
    pts.push(`BAU is flat: ${forecast.base.label} ran ${n0(bau)} a day. A peak uplift of ${n1(plan.growth_pct)}% on top of a flat base is a step change in peak intensity, not the trend carrying us there.`);
  }
  if (a.busiest[0]) {
    pts.push(`The plan is decided by one day, not by a monthly average: ${fmtDay(a.busiest[0].iso)} carried ${n0(a.busiest[0].parcels)} on ${a.busiest[0].drivers} logins — ${n0(a.busiest[0].perDriver)} articles each, well above the ${n0(plan && plan.articles_per_driver)} planning rate.`);
  }
  if (forecast && forecast.peakDay) {
    pts.push(`Grown by ${n1(plan.growth_pct)}%, that day becomes ${n0(forecast.peakDay.parcels)} parcels — ${n1(forecast.peakDay.driversAtLastRate)} drivers at last December's demonstrated rate, ${n1(forecast.peakDay.driversNeeded)} at the planning rate. The plan currently has ${forecast.drivers.total}.`);
  }
  const lastWeek = a.weeks.length ? a.weeks.reduce((m, w) => (w.total > m.total ? w : m), a.weeks[0]) : null;
  if (lastWeek) {
    pts.push(`Peak week was w/c ${fmtDate(lastWeek.wc)} at ${n0(lastWeek.total)} parcels. Resources need to be in place a fortnight before it, not during it.`);
  }
  return (
    <div style={{ ...card, borderColor: 'var(--hp-gold)' }}>
      <h3 style={sectionTitle}>Talking points for the meeting</h3>
      <ul style={{ margin: 0, paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 8, fontSize: '.85rem', lineHeight: 1.6 }}>
        {pts.map((p, i) => <li key={i}>{p}</li>)}
      </ul>
    </div>
  );
}

// ── Scope of works ───────────────────────────────────────────────────────────
const STATUSES = [['todo', 'To do', 'var(--hp-dim)'], ['doing', 'In progress', 'var(--hp-amber)'], ['done', 'Done', 'var(--hp-green)'], ['na', 'Not required', 'var(--hp-muted)']];

function ScopeTab({ scope, year, onChange, onReset }) {
  const streams = useMemo(() => {
    const m = new Map();
    scope.forEach((t, i) => {
      const list = m.get(t.stream) || [];
      list.push({ ...t, _i: i });
      m.set(t.stream, list);
    });
    return [...m.entries()];
  }, [scope]);

  const live = scope.filter((t) => t.status !== 'na');
  const done = live.filter((t) => t.status === 'done').length;
  const overdue = live.filter((t) => t.status !== 'done' && t.due && t.due < new Date().toISOString().slice(0, 10));

  const set = (i, patch) => onChange(scope.map((t, k) => (k === i ? { ...t, ...patch } : t)));
  const add = () => onChange([...scope, { id: `t_${Date.now()}`, stream: 'Other', title: '', detail: '', owner: '', due: `${year}-10-31`, status: 'todo', note: '' }]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <Stat icon={ClipboardList} title={`Peak ${year} readiness`} value={`${done}/${live.length}`} sub={`${live.length - done} workstream(s) still open`} tone={done === live.length ? 'var(--hp-green)' : 'var(--hp-gold)'} />
        <Stat icon={AlertTriangle} title="Past due" value={overdue.length} sub={overdue.length ? overdue.map((t) => t.title).slice(0, 2).join(' · ') : 'nothing overdue'} tone={overdue.length ? 'var(--hp-red)' : 'var(--hp-green)'} />
      </div>

      {streams.map(([stream, tasks]) => (
        <div key={stream} style={card}>
          <h3 style={sectionTitle}>{stream}</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {tasks.map((t) => {
              const meta = STATUSES.find(([k]) => k === t.status) || STATUSES[0];
              const late = t.status !== 'done' && t.status !== 'na' && t.due && t.due < new Date().toISOString().slice(0, 10);
              return (
                <div key={t.id} style={{ borderLeft: `3px solid ${meta[2]}`, paddingLeft: 12 }}>
                  <input className="input" style={{ fontWeight: 700, marginBottom: 6 }} value={t.title} placeholder="Workstream"
                    onChange={(e) => set(t._i, { title: e.target.value })} />
                  {t.detail && <div style={{ fontSize: '.78rem', color: 'var(--hp-dim)', lineHeight: 1.5, marginBottom: 8 }}>{t.detail}</div>}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 8 }}>
                    <div><span style={label}>Owner</span><input className="input" style={wide} value={t.owner || ''} onChange={(e) => set(t._i, { owner: e.target.value })} /></div>
                    <div><span style={label}>Due{late ? ' · overdue' : ''}</span>
                      <input className="input" type="date" style={late ? { ...wide, borderColor: 'var(--hp-red)' } : wide} value={t.due || ''} onChange={(e) => set(t._i, { due: e.target.value })} />
                    </div>
                    <div><span style={label}>Status</span>
                      <select className="input" style={wide} value={t.status} onChange={(e) => set(t._i, { status: e.target.value })}>
                        {STATUSES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                      </select>
                    </div>
                    <div style={{ gridColumn: '1 / -1' }}><span style={label}>Note</span>
                      <input className="input" style={wide} value={t.note || ''} placeholder="Where it is up to" onChange={(e) => set(t._i, { note: e.target.value })} />
                    </div>
                  </div>
                  <div style={{ marginTop: 6, textAlign: 'right' }}>
                    <button className="btn btn-secondary btn-sm" onClick={() => onChange(scope.filter((_, k) => k !== t._i))} title="Remove"><Trash2 size={13} /></button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button className="btn btn-secondary btn-sm" onClick={add}><Plus size={14} /> Add workstream</button>
        <button className="btn btn-secondary btn-sm" onClick={onReset}><RotateCcw size={14} /> Reset to the standard peak scope</button>
      </div>
    </div>
  );
}
